import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import SideBar from "../components/SideBar";
import DetailsHeaderBar from "../components/DetailsHeaderBar";
import DetailSection from "../components/DetailSection";
import {
  MdOutlineAccountBalance,
  MdOutlineStorefront,
  MdProductionQuantityLimits,
} from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { API_URL } from "../constant";
import { MdOutlineQrCode } from "react-icons/md";
import { TbUserSquare } from "react-icons/tb";
import { IoMdGift } from "react-icons/io";
import { FaBan, FaCheckCircle } from "react-icons/fa";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

function PromotionDetails() {
  const location = useLocation();
  const navigate = useNavigate();

  const initialPromotion = location.state?.promotion;
  const [promotion, setPromotion] = useState(initialPromotion || null);
  const [loading, setLoading] = useState(!initialPromotion);
  const [error, setError] = useState("");
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printCount, setPrintCount] = useState(1);
  const [actionLoading, setActionLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  // Snackbar states
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API_URL ,
    });
    instance.interceptors.request.use((config) => {
      const token = localStorage.getItem("auth_token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        config.headers.Accept = "application/json";
      }
      return config;
    });
    return instance;
  }, []);

  useEffect(() => {
    if (promotion) return; // already have it from navigation state

    const searchParams = new URLSearchParams(location.search);
    const idFromQuery = searchParams.get("id");
    const idFromState = initialPromotion?.id;
    const promoId = idFromQuery || idFromState;

    if (!promoId) {
      setLoading(false);
      setError("No promotion id provided");
      return;
    }

    const fetchDetails = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.get(`/superadmin/promos/${promoId}`);
        const data = res?.data?.data ?? res?.data ?? null;
        setPromotion(data);
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load promotion";
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [api, initialPromotion, location.search, promotion]);

  const pad = (v) => String(v ?? "").trim();
  const displayVal = (v) =>
    v === undefined || v === null || v === "" ? "-" : v;

  const supplier = promotion?.supplier || promotion?.supplierDetails || {};
  const retail =
    promotion?.retail_business ||
    promotion?.retailBusinessDetails ||
    promotion?.retail ||
    {};

  const agentId =
    promotion?.agent_id ||
    promotion?.business_ad_agent_no ||
    promotion?.agent?.id;
  const agentSource =
    promotion?.agent ||
    promotion?.business_ad_agent ||
    promotion?.businessAdAgentDetails ||
    {};
  const agentName =
    agentSource?.advertising_agent_name ||
    agentSource?.name ||
    [agentSource?.first_name, agentSource?.last_name].filter(Boolean).join(" ");
  const agentEmail =
    agentSource?.business_email ||
    agentSource?.advertising_agent_email ||
    agentSource?.contact_email ||
    agentSource?.email;
  const qty = promotion?.quantity ?? promotion?.Quantity;
  const expiry = promotion?.expires_at;
  const statusVal = promotion?.status || promotion?.Status;
  const productName = promotion?.product?.name || promotion?.Product;
  const uniqueCode = promotion?.unique_code || promotion?.UniqueID;

  const qrValue =
    promotion?.qr_code_url ||
    promotion?.qr_code ||
    promotion?.qrCode ||
    promotion?.qr ||
    promotion?.QRCode ||
    promotion?.label ||
    promotion?.unique_code ||
    `${pad(promotion?.Supplier)}-${pad(promotion?.BusinessAdAgentNo)}-${pad(promotion?.RetailBusiness)}-${pad(promotion?.Product)}-${pad(promotion?.TopCoin)}-${pad(promotion?.UniqueID)}-1`;

  const printable = useMemo(() => {
    return {
      qrValue,
      label: uniqueCode || qrValue || "Promotion QR",
    };
  }, [qrValue, uniqueCode]);

  const printPages = useMemo(() => {
    const total = Math.max(1, parseInt(printCount, 10) || 1);
    const copies = Array.from({ length: total }, (_, idx) => idx);
    const pages = [];
    for (let i = 0; i < copies.length; i += 12) {
      pages.push(copies.slice(i, i + 12));
    }
    return pages;
  }, [printCount]);

  const goToEdit = () => {
    if (!promotion) return;
    navigate("/edit-promotion", { state: { promotion } });
  };

  const handlePrintQR = () => {
    setShowPrintModal(true);
  };

  const confirmPrint = () => {
    const qty = Math.max(1, parseInt(printCount, 10) || 1);

    // Check if print quantity exceeds available stock
    if (qty > Number(promotion?.quantity)) {
      setSnackbarMessage(
        `Make Sure You have enough product Qty. Available: ${promotion?.quantity}`,
      );
      setSnackbarSeverity("warning");
      setOpenSnackbar(true);
      return; // Don't proceed with printing
    }

    setPrintCount(qty);
    setShowPrintModal(false);
    setTimeout(() => {
      window.print();
    }, 0);
  };
  const cancelPrint = () => setShowPrintModal(false);

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setOpenSnackbar(false);
  };

  // Cancel promotion function
  const handleCancelPromotion = async () => {
    if (!promotion?.id) return;

    setActionLoading(true);
    try {
      const response = await api.post(
        `/superadmin/promos/${promotion.id}/cancel`,
        {
          reason: cancelReason || "SuperAdmin cancellation",
        },
      );

      if (response.data?.success) {
        // Refresh promotion data
        const refreshRes = await api.get(`/superadmin/promos/${promotion.id}`);
        const updatedPromo = refreshRes?.data?.data ?? refreshRes?.data ?? null;
        setPromotion(updatedPromo);

        // Show success message with MUI Alert
        setSnackbarMessage("Promotion cancelled successfully!");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to cancel promotion";
      setSnackbarMessage(`Error: ${msg}`);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setActionLoading(false);
      setShowCancelConfirm(false);
      setCancelReason("");
    }
  };

  // Complete promotion function
  const handleCompletePromotion = async () => {
    if (!promotion?.id) return;

    setActionLoading(true);
    try {
      const response = await api.post(
        `/superadmin/promos/${promotion.id}/complete`,
      );

      if (response.data?.success) {
        // Refresh promotion data
        const refreshRes = await api.get(`/superadmin/promos/${promotion.id}`);
        const updatedPromo = refreshRes?.data?.data ?? refreshRes?.data ?? null;
        setPromotion(updatedPromo);

        // Show success message with MUI Alert
        setSnackbarMessage("Promotion marked as completed successfully!");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to complete promotion";
      setSnackbarMessage(`Error: ${msg}`);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setActionLoading(false);
      setShowCompleteConfirm(false);
    }
  };

  // Helper function to check if action is allowed based on status
  const canCancel = () => {
    const status = statusVal?.toLowerCase();
    return !["used", "expired", "cancelled", "completed"].includes(status);
  };

  const canComplete = () => {
    const status = statusVal?.toLowerCase();
    return ["sold", "assigned"].includes(status);
  };

  if (loading) {
    return (
      <div className="manage-member-layout">
        <SideBar />
        <div className="product-details-main" style={{ padding: "24px" }}>
          <p style={{ marginBottom: "12px" }}>Loading promotion...</p>
        </div>
      </div>
    );
  }

  if (!promotion || error) {
    return (
      <div className="manage-member-layout">
        <SideBar />
        <div className="product-details-main" style={{ padding: "24px" }}>
          <p style={{ marginBottom: "12px" }}>
            {error ||
              "No promotion data found. Please go back and select a promotion."}
          </p>
          <button className="btn" onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-member-layout">
      <style>
        {`
                    @media print {
                        body * { visibility: hidden; }
                        #qr-print-area, #qr-print-area * { visibility: visible; }
                        #qr-print-area { display: block !important; position: absolute; left: 0; top: 0; width: 100%; }
                    }
                `}
      </style>
      <SideBar />
      <div className="product-details-main">
        {/* MUI Snackbar for success/error messages */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MuiAlert
            severity={snackbarSeverity}
            icon={
              snackbarSeverity === "success" ? (
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "18px",
                    color: "rgba(51, 178, 51, 1)",
                  }}
                >
                  ✔
                </span>
              ) : snackbarSeverity === "warning" ? (
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "18px",
                    color: "#ff0000", // Red color for warning icon
                  }}
                >
                  ⚠
                </span>
              ) : snackbarSeverity === "error" ? (
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "18px",
                    color: "#dc3545",
                  }}
                >
                  ✘
                </span>
              ) : null
            }
            sx={{
              backgroundColor: "#ffffff", // White background
              color: "#ff0000", // Red text color
              border:
                snackbarSeverity === "success"
                  ? "2px solid rgba(51, 178, 51, 1)"
                  : snackbarSeverity === "warning"
                    ? "2px solid #ff0000" // Red border for warning
                    : snackbarSeverity === "error"
                      ? "2px solid #dc3545"
                      : "none",
              borderRadius: "10px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
              fontSize: "15px",
              padding: "8px 12px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              height: "auto",
              minHeight: "25px",
              width: "100%",
              textAlign: "center",
              justifyContent: "center",
              "& .MuiAlert-message": {
                color: "#ff0000", // Ensure message text is red
                padding: 0,
              },
            }}
          >
            {snackbarMessage}
          </MuiAlert>
        </Snackbar>

        <DetailsHeaderBar
          title="Promotion Details"
          subtitle="You can edit the promotion details to update the promotion details"
          breadcrumbs={[
            { label: "Home", path: "/manage-member" },
            { label: "Manage Promotion", path: "/manage-promotion" },
            { label: "Promotion Details" },
          ]}
        />

        <DetailSection
          title="Basic Details"
          icon={IoMdGift}
          showEdit={false}
          onEdit={goToEdit}
          extraActions={
            <div style={{ display: "flex", gap: "10px" }}>
              <button className="edit-btn" onClick={handlePrintQR}>
                Print QR
              </button>
              {canCancel() && (
                <button
                  className="edit-btn"
                  onClick={() => setShowCancelConfirm(true)}
                  style={{ backgroundColor: "#dc3545", color: "white" }}
                  disabled={actionLoading}
                >
                  <FaBan style={{ marginRight: "5px" }} /> Cancel Promotion
                </button>
              )}
              {canComplete() && (
                <button
                  className="edit-btn"
                  onClick={() => setShowCompleteConfirm(true)}
                  style={{ backgroundColor: "#28a745", color: "white" }}
                  disabled={actionLoading}
                >
                  <FaCheckCircle style={{ marginRight: "5px" }} /> Complete
                  Promotion
                </button>
              )}
            </div>
          }
          fields={[
            { label: "UniqueID", value: displayVal(uniqueCode) },
            { label: "Product", value: displayVal(productName) },
            {
              label: "TopCoin",
              value: displayVal(
                promotion?.topcoin_amount ?? promotion?.TopCoin,
              ),
            },
            { label: "Quantity", value: displayVal(qty) },
            { label: "Expiry Date", value: displayVal(expiry) },
            { label: "Status", value: displayVal(statusVal) },
          ]}
        />

        <DetailSection
          title="Supplier Details"
          icon={MdOutlineStorefront}
          showEdit={false}
          fields={[
            { label: "ID", value: displayVal(supplier.id) },
            { label: "Name", value: displayVal(supplier.retail_name) },
            { label: "Email", value: displayVal(supplier.email) },
          ]}
        />
        <DetailSection
          title="Retail Business Details"
          icon={MdOutlineAccountBalance}
          showEdit={false}
          fields={[
            { label: "ID", value: displayVal(retail.id) },
            {
              label: "Name",
              value: displayVal(retail.first_name + " " + retail.last_name),
            },
            { label: "Email", value: displayVal(retail.email) },
          ]}
        />
        <DetailSection
          title="BusinessAdAgent Details"
          icon={TbUserSquare}
          showEdit={false}
          fields={[
            {
              label: "ID",
              value: displayVal(
                agentSource?.agent_id || agentSource?.id || agentId,
              ),
            },
            { label: "Name", value: displayVal(agentName) },
            { label: "Email", value: displayVal(agentEmail) },
          ]}
        />
        <DetailSection
          title="QR Code"
          icon={MdOutlineQrCode}
          showEdit={false}
          fields={[
            { label: "QR Value", value: displayVal(qrValue) },
            {
              label: "QR Code",
              value: (
                <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
                  <QRCodeSVG
                    value={qrValue}
                    style={{
                      objectFit: "cover",
                      borderRadius: 8,
                      border: "1px solid #ddd",
                      cursor: "pointer",
                    }}
                  />
                </div>
              ),
            },
          ]}
        />
      </div>

      {/* Print QR Modal */}
      {showPrintModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "20px",
              width: "320px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <div style={{ fontSize: "18px", fontWeight: 700 }}>
              Print quantity
            </div>
            <label
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                fontSize: "14px",
              }}
            >
              <span>How many copies do you want to print?</span>
              <input
                type="number"
                min={1}
                value={printCount}
                onChange={(e) => setPrintCount(e.target.value)}
                style={{
                  padding: "10px",
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                  fontSize: "14px",
                }}
              />
            </label>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <button
                onClick={cancelPrint}
                style={{
                  padding: "10px 14px",
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                  background: "#f5f5f5",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmPrint}
                style={{
                  padding: "10px 14px",
                  borderRadius: "8px",
                  border: "none",
                  background: "#6d31ed",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                Print
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "20px",
              width: "380px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <div
              style={{ fontSize: "18px", fontWeight: 700, color: "#dc3545" }}
            >
              <FaBan style={{ marginRight: "8px" }} /> Cancel Promotion
            </div>
            <p style={{ margin: "10px 0", fontSize: "14px", color: "#666" }}>
              Are you sure you want to cancel this promotion? This action cannot
              be undone.
            </p>
            <label
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                fontSize: "14px",
              }}
            >
              <span>Reason for cancellation (optional):</span>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Enter reason for cancellation..."
                rows={3}
                style={{
                  padding: "10px",
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                  fontSize: "14px",
                  resize: "vertical",
                }}
              />
            </label>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                marginTop: "10px",
              }}
            >
              <button
                onClick={() => {
                  setShowCancelConfirm(false);
                  setCancelReason("");
                }}
                disabled={actionLoading}
                style={{
                  padding: "10px 14px",
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                  background: "#f5f5f5",
                  cursor: actionLoading ? "not-allowed" : "pointer",
                  opacity: actionLoading ? 0.6 : 1,
                }}
              >
                No, Go Back
              </button>
              <button
                onClick={handleCancelPromotion}
                disabled={actionLoading}
                style={{
                  padding: "10px 14px",
                  borderRadius: "8px",
                  border: "none",
                  background: "#dc3545",
                  color: "#fff",
                  cursor: actionLoading ? "not-allowed" : "pointer",
                  opacity: actionLoading ? 0.6 : 1,
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                {actionLoading ? "Processing..." : "Yes, Cancel Promotion"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Confirmation Modal */}
      {showCompleteConfirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "20px",
              width: "380px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <div
              style={{ fontSize: "18px", fontWeight: 700, color: "#28a745" }}
            >
              <FaCheckCircle style={{ marginRight: "8px" }} /> Complete
              Promotion
            </div>
            <p style={{ margin: "10px 0", fontSize: "14px", color: "#666" }}>
              Are you sure you want to mark this promotion as completed? This
              will distribute TopCoins to all parties involved.
            </p>
            <div
              style={{
                background: "#f8f9fa",
                padding: "10px",
                borderRadius: "8px",
                fontSize: "14px",
              }}
            >
              <p>
                <strong>TopCoin Amount:</strong>{" "}
                {displayVal(promotion?.topcoin_amount ?? promotion?.TopCoin)}
              </p>
              <p>
                <strong>Current Status:</strong> {displayVal(statusVal)}
              </p>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                marginTop: "10px",
              }}
            >
              <button
                onClick={() => setShowCompleteConfirm(false)}
                disabled={actionLoading}
                style={{
                  padding: "10px 14px",
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                  background: "#f5f5f5",
                  cursor: actionLoading ? "not-allowed" : "pointer",
                  opacity: actionLoading ? 0.6 : 1,
                }}
              >
                No, Go Back
              </button>
              <button
                onClick={handleCompletePromotion}
                disabled={actionLoading}
                style={{
                  padding: "10px 14px",
                  borderRadius: "8px",
                  border: "none",
                  background: "#28a745",
                  color: "#fff",
                  cursor: actionLoading ? "not-allowed" : "pointer",
                  opacity: actionLoading ? 0.6 : 1,
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                {actionLoading ? "Processing..." : "Yes, Complete Promotion"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden print-only QR */}
      {printable && (
        <div id="qr-print-area" style={{ display: "none" }}>
          {printPages.map((page, pageIdx) => (
            <div
              key={pageIdx}
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "14px",
                padding: "18px",
                width: "210mm",
                minHeight: "297mm",
                boxSizing: "border-box",
                justifyItems: "center",
                alignItems: "center",
                pageBreakAfter:
                  pageIdx === printPages.length - 1 ? "auto" : "always",
              }}
            >
              {page.map((itemIdx) => (
                <div
                  key={itemIdx}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "10px",
                    padding: "12px",
                    border: "1px solid #ddd",
                    borderRadius: "10px",
                    pageBreakInside: "avoid",
                  }}
                >
                  <QRCodeSVG value={printable.qrValue} size={150} />
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default PromotionDetails;
